import { useState, useEffect } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import { updateProfile } from "../lib/unified-auth";
import { supabase } from "../lib/supabase";
import { Eye, EyeOff, Building2, Upload, ShoppingCart } from "lucide-react";
import { fetchUserOrders, type PortalOrder } from "../lib/portal-api";

export default function AccountPage() {
  const { identity } = useIdentity();
  const [name, setName] = useState(identity.name ?? "");
  const [email, setEmail] = useState(identity.email ?? "");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  // Password change
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pwStatus, setPwStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [pwError, setPwError] = useState<string | null>(null);

  // Company logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoStatus, setLogoStatus] = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [logoError, setLogoError] = useState<string | null>(null);

  // Order history
  const [orders, setOrders] = useState<PortalOrder[]>([]);

  useEffect(() => {
    if (identity.userId) {
      fetchUserOrders(identity.userId).then(setOrders);
    }
  }, [identity.userId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!identity.userId) return;
    setStatus("saving");
    setError(null);
    const { error: err } = await updateProfile(identity.userId, { name, email });
    if (err) {
      setError(err);
      setStatus("error");
    } else {
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2000);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (!newPassword) {
      setPwError("Please enter a new password.");
      setPwStatus("error");
      return;
    }
    if (newPassword.length < 6) {
      setPwError("Password must be at least 6 characters.");
      setPwStatus("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("Passwords do not match.");
      setPwStatus("error");
      return;
    }
    setPwStatus("saving");
    setPwError(null);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    if (err) {
      setPwError(err.message);
      setPwStatus("error");
    } else {
      setPwStatus("saved");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPwStatus("idle"), 3000);
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Logo must be under 2MB.");
      setLogoStatus("error");
      return;
    }
    setLogoStatus("uploading");
    setLogoError(null);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "png";
      const path = `company-logos/${identity.organizationId ?? identity.userId}/logo.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("company-assets")
        .upload(path, file, { upsert: true });

      if (uploadErr) {
        // If bucket doesn't exist, store as data URL fallback
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          setLogoUrl(dataUrl);
          setLogoStatus("done");
          setTimeout(() => setLogoStatus("idle"), 3000);
        };
        reader.readAsDataURL(file);
        return;
      }

      const { data: urlData } = supabase.storage
        .from("company-assets")
        .getPublicUrl(path);

      setLogoUrl(urlData.publicUrl);
      setLogoStatus("done");
      setTimeout(() => setLogoStatus("idle"), 3000);
    } catch {
      setLogoError("Upload failed. Please try again.");
      setLogoStatus("error");
    }
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Account Settings</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Manage your profile, password, and company branding.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-5)" }}>
        {/* Profile form */}
        <div className="card" style={{ padding: "var(--space-5)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)" }}>Profile</h2>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label className="form-label" htmlFor="account-name">Full Name</label>
              <input
                id="account-name"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="account-email">Email</label>
              <input
                id="account-email"
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && <p className="form-error">{error}</p>}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={status === "saving"}
              style={{ marginTop: "var(--space-3)" }}
            >
              {status === "saving" ? "Saving..." : status === "saved" ? "Saved!" : "Save Changes"}
            </button>
          </form>
        </div>

        {/* Password change */}
        <div className="card" style={{ padding: "var(--space-5)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)" }}>Change Password</h2>
          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label className="form-label" htmlFor="new-password">New Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  style={{ paddingRight: "2.5rem" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "0.625rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--text-muted)",
                    padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="confirm-password">Confirm New Password</label>
              <input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
              />
            </div>
            {pwError && <p className="form-error">{pwError}</p>}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={pwStatus === "saving"}
              style={{ marginTop: "var(--space-3)" }}
            >
              {pwStatus === "saving" ? "Updating..." : pwStatus === "saved" ? "Password Updated!" : "Update Password"}
            </button>
          </form>
        </div>

        {/* Company logo */}
        {identity.organizationId && (
          <div className="card" style={{ padding: "var(--space-5)" }}>
            <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Building2 size={18} /> Company Logo
            </h2>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
              Upload your company logo to appear on customized certificates and your portal pages.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company logo"
                  style={{ maxWidth: "120px", maxHeight: "120px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}
                />
              ) : (
                <div style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "var(--radius-md)",
                  border: "2px dashed var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-muted)",
                }}>
                  <Building2 size={32} />
                </div>
              )}
              <div>
                <label
                  htmlFor="logo-upload"
                  className="btn btn-secondary"
                  style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.375rem" }}
                >
                  <Upload size={14} />
                  {logoStatus === "uploading" ? "Uploading..." : "Upload Logo"}
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  style={{ display: "none" }}
                />
                {logoStatus === "done" && (
                  <p style={{ fontSize: "0.8125rem", color: "var(--accent-600)", marginTop: "0.5rem", fontWeight: 600 }}>
                    Logo uploaded!
                  </p>
                )}
                {logoError && (
                  <p style={{ fontSize: "0.8125rem", color: "var(--error-600)", marginTop: "0.5rem" }}>
                    {logoError}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order history */}
        <div className="card" style={{ padding: "var(--space-5)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <ShoppingCart size={18} /> Order History
          </h2>
          {orders.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No orders yet. Browse the certification catalog to purchase courses.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Date", "Item", "Amount", "Status", "Receipt"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.8125rem" }}>{new Date(o.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.8125rem", fontWeight: 600 }}>{o.item_name}</td>
                      <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.8125rem" }}>${(o.amount / 100).toFixed(2)}</td>
                      <td style={{ padding: "0.5rem 0.75rem" }}>
                        <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", fontWeight: 600, background: o.status === "paid" ? "var(--accent-50)" : "var(--warning-50)", color: o.status === "paid" ? "var(--accent-600)" : "var(--warning-600)" }}>{o.status}</span>
                      </td>
                      <td style={{ padding: "0.5rem 0.75rem" }}>
                        {o.receipt_url ? (
                          <a href={o.receipt_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.75rem", color: "var(--accent-600)" }}>Download</a>
                        ) : (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Identity & session info */}
        <div className="card" style={{ padding: "var(--space-5)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)" }}>Identity & Session</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>User ID</div>
              <div style={{ fontSize: "0.875rem", fontFamily: "monospace" }}>{identity.userId ?? "N/A"}</div>
            </div>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Roles</div>
              <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
                {identity.roles.map((role) => (
                  <span
                    key={role}
                    style={{
                      padding: "0.125rem 0.5rem",
                      borderRadius: "var(--radius-full)",
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      background: "var(--accent-50)",
                      color: "var(--accent-600)",
                      textTransform: "capitalize",
                    }}
                  >
                    {role.replace("_", " ")}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Organization</div>
              <div style={{ fontSize: "0.875rem" }}>{identity.organizationId ?? "None"}</div>
            </div>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Active Licenses</div>
              <div style={{ fontSize: "0.875rem" }}>{identity.activeLicenses.length} license(s)</div>
            </div>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--neutral-500)", marginBottom: "0.25rem" }}>Session</div>
              <div style={{ fontSize: "0.875rem", color: identity.sessionToken ? "var(--accent-600)" : "var(--text-muted)" }}>
                {identity.sessionToken ? "Active" : "No session token"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .account-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PlatformLayout>
  );
}
